/**
 * GrapesEmailEditor — drag-and-drop email builder using GrapesJS + newsletter preset.
 *
 * Drop-in replacement for CKEditor in TemplateEditor.
 * Outputs email-safe HTML (table-based layout, inline styles).
 *
 * ─── Swappable with Unlayer (react-email-editor) ────────────────────────────
 * Unlayer is the industry gold standard (Mailchimp uses it internally).
 * To swap: npm install @unlayer/react, replace this component with an
 * UnlayerEditor wrapper that calls editor.exportHtml() on change.
 * One-time license: https://unlayer.com/embed — ~$149/mo or custom deal.
 * The interface this component exposes (value/onChange) is identical to
 * what an Unlayer wrapper would expose, so the swap is one file change.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useEffect, useRef, useState } from "react";

// Velaro template variable tokens — injected via toolbar button
const VELARO_VARS = [
  { label: "First Name", token: "<<contact.firstName>>" },
  { label: "Last Name",  token: "<<contact.lastName>>" },
  { label: "Email",      token: "<<contact.email>>" },
  { label: "Company",    token: "<<contact.company>>" },
  { label: "Unsubscribe", token: "{{unsubscribe_url}}" },
];

interface Props {
  value: string;
  onChange: (html: string) => void;
  uploadUrl?: string;
}

export default function GrapesEmailEditor({ value, onChange, uploadUrl }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef    = useRef<any>(null);
  const [preview, setPreview] = useState<"desktop" | "mobile">("desktop");
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [darkPreview, setDarkPreview] = useState(false);
  const [inited, setInited]   = useState(false);
  const [varInsert, setVarInsert] = useState(false);

  useEffect(() => {
    if (!containerRef.current || inited) return;
    setInited(true);

    let grapesjs: any;
    let newsletterPreset: any;

    Promise.all([
      import("grapesjs"),
      import("grapesjs-preset-newsletter"),
    ]).then(([gjs, preset]) => {
      grapesjs       = gjs.default;
      newsletterPreset = preset.default;

      const editor = grapesjs.init({
        container: containerRef.current!,
        height: "520px",
        width: "100%",
        storageManager: false,
        plugins: [newsletterPreset],
        pluginsOpts: {
          [newsletterPreset as any]: {
            modalLabelImport: "Paste your HTML here",
            modalLabelExport: "Export HTML",
            codeViewerTheme: "hopscotch",
            imageEditor: false,
          },
        },
        assetManager: uploadUrl ? {
          upload: uploadUrl,
          uploadName: "file",
        } : undefined,
        // Email-safe defaults
        components: value || defaultTemplate,
        style: "",
      });

      editorRef.current = editor;

      // Propagate changes upward
      editor.on("update", () => {
        onChange(editor.getHtml());
      });

      // Add Velaro variable blocks to the block panel
      VELARO_VARS.forEach(v => {
        editor.BlockManager.add(`velaro-var-${v.token}`, {
          label: v.label,
          category: "Velaro Variables",
          content: `<span style="font-family:inherit">${v.token}</span>`,
          attributes: { class: "gjs-block-section" },
        });
      });
    });

    return () => {
      editorRef.current?.destroy?.();
      editorRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePreview = () => {
    if (editorRef.current) {
      setPreviewHtml(editorRef.current.getHtml());
    }
    setShowPreview(true);
  };

  const insertVar = (token: string) => {
    if (editorRef.current) {
      const selected = editorRef.current.getSelected();
      if (selected) {
        const cur = selected.get("content") || "";
        selected.set("content", cur + token);
      }
    }
    setVarInsert(false);
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Toolbar row */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200 flex-wrap">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Design Editor</span>
        <div className="flex-1" />

        {/* Variable insert */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setVarInsert(v => !v)}
            className="px-2 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 font-mono"
          >
            {"{ } Insert Variable"}
          </button>
          {varInsert && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-20 min-w-[180px]">
              {VELARO_VARS.map(v => (
                <button
                  key={v.token}
                  type="button"
                  onClick={() => insertVar(v.token)}
                  className="block w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 font-mono text-gray-700"
                >
                  {v.token}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Preview */}
        <button
          type="button"
          onClick={handlePreview}
          className="px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-50 text-gray-600 flex items-center gap-1"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          Preview
        </button>
      </div>

      {/* GrapesJS canvas */}
      <div ref={containerRef} />

      {/* Preview modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl flex flex-col" style={{ width: "90vw", maxWidth: 900, maxHeight: "90vh" }}>
            {/* Preview toolbar */}
            <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-200">
              <span className="font-semibold text-gray-900 text-sm">Email Preview</span>
              <div className="flex-1" />

              {/* Device toggle */}
              <div className="flex border border-gray-200 rounded overflow-hidden text-xs">
                {(["desktop", "mobile"] as const).map(d => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setPreview(d)}
                    className={`px-3 py-1.5 flex items-center gap-1 ${preview === d ? "bg-gray-900 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                  >
                    {d === "desktop"
                      ? <><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg> Desktop</>
                      : <><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="5" y="2" width="14" height="20" rx="2"/><circle cx="12" cy="18" r="1" fill="currentColor"/></svg> Mobile</>
                    }
                  </button>
                ))}
              </div>

              {/* Dark mode toggle */}
              <button
                type="button"
                onClick={() => setDarkPreview(v => !v)}
                className={`px-3 py-1.5 text-xs flex items-center gap-1 border rounded ${darkPreview ? "bg-gray-900 text-white border-gray-700" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
                Dark Mode
              </button>

              {/* Inbox preview hint */}
              <div className="text-xs text-gray-400 hidden md:block">
                Gmail subject preview: <span className="text-gray-700 font-medium">Your email subject line here</span>
                <span className="text-gray-400"> · 48 chars max visible</span>
              </div>

              <button type="button" onClick={() => setShowPreview(false)} className="text-gray-400 hover:text-gray-600 text-xl ml-2" aria-label="Close preview">×</button>
            </div>

            {/* Gmail inbox preview bar */}
            <div className="px-5 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">Y</div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-gray-800 truncate">From: Your Company &lt;hello@yourdomain.com&gt;</p>
                <p className="text-xs text-gray-500 truncate">Your email subject line · Preview text shows here — first ~90 chars of body text</p>
              </div>
              <div className="ml-auto text-xs text-gray-400 flex-shrink-0">Just now</div>
            </div>

            {/* Preview iframe */}
            <div className="flex-1 overflow-auto flex justify-center items-start bg-gray-100 p-6">
              <div
                style={{
                  width: preview === "mobile" ? 375 : "100%",
                  maxWidth: preview === "desktop" ? 680 : 375,
                  transition: "width 0.2s",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
                  borderRadius: 8,
                  overflow: "hidden",
                  background: darkPreview ? "#1a1a1a" : "#ffffff",
                  filter: darkPreview ? "invert(1) hue-rotate(180deg)" : "none",
                }}
              >
                <div
                  dangerouslySetInnerHTML={{ __html: previewHtml || "<p style='padding:40px;color:#999;text-align:center'>No content yet.</p>" }}
                />
              </div>
            </div>

            {/* Preview footer */}
            <div className="px-5 py-3 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-400">
              <span>💡 Dark mode preview simulates how email clients invert colors. Test on real devices before sending.</span>
              <div className="flex-1" />
              <span className={`px-2 py-0.5 rounded font-medium ${preview === "mobile" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
                {preview === "mobile" ? "375px wide" : "680px wide"}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Default email template that ships in the editor ───────────────────────────
const defaultTemplate = `
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f4;padding:40px 20px">
  <tr><td align="center">
    <table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden">
      <tr><td style="background:#1a56db;padding:32px 40px;text-align:center">
        <p style="color:#ffffff;font-size:24px;font-weight:700;margin:0;font-family:Arial,sans-serif">Your Company Name</p>
      </td></tr>
      <tr><td style="padding:40px">
        <p style="font-size:22px;font-weight:700;color:#111827;margin:0 0 16px;font-family:Arial,sans-serif">Hi <<contact.firstName>>,</p>
        <p style="font-size:16px;color:#374151;line-height:1.6;margin:0 0 24px;font-family:Arial,sans-serif">
          We have something exciting to share with you. Click the button below to learn more.
        </p>
        <table cellpadding="0" cellspacing="0">
          <tr><td style="background:#1a56db;border-radius:6px;padding:14px 28px">
            <a href="#" style="color:#ffffff;font-size:16px;font-weight:600;text-decoration:none;font-family:Arial,sans-serif">Get Started →</a>
          </td></tr>
        </table>
        <p style="font-size:14px;color:#6b7280;margin:32px 0 0;font-family:Arial,sans-serif">
          Questions? Reply to this email anytime.
        </p>
      </td></tr>
      <tr><td style="background:#f9fafb;padding:24px 40px;text-align:center;border-top:1px solid #e5e7eb">
        <p style="font-size:12px;color:#9ca3af;margin:0 0 8px;font-family:Arial,sans-serif">
          You received this because you signed up at ourwebsite.com
        </p>
        <p style="font-size:12px;color:#9ca3af;margin:0;font-family:Arial,sans-serif">
          <a href="{{unsubscribe_url}}" style="color:#9ca3af">Unsubscribe</a> · 123 Business St, Your City
        </p>
      </td></tr>
    </table>
  </td></tr>
</table>`;
